import { createReducer, on } from '@ngrx/store';
import * as AppLifecyclesActions from './app-lifecycles.actions';
import {
  isSessionExpired,
  isSessionTracking,
  isSessionWarning,
} from './app-lifecycles.helpers';
import { AppLifecyclesState, SessionState } from './app-lifecycles.models';
import { DEFAULT_SESSION_TIMEOUT_CONFIG } from './session-timeout.config';

export const APP_LIFECYCLES_FEATURE_KEY = 'appLifecycles';

/**
 * `lastActivityAt: 0` means "not tracking". The timings are placeholders until
 * `sessionTrackingStarted` copies in the injected config at login.
 */
export const initialSessionState: SessionState = {
  lastActivityAt: 0,
  nowMs: 0,
  idleWarningAfterMs: DEFAULT_SESSION_TIMEOUT_CONFIG.idleWarningAfterMs,
  warningDurationMs: DEFAULT_SESSION_TIMEOUT_CONFIG.warningDurationMs,
};

export const initialAppLifecyclesState: AppLifecyclesState = {
  preAuth: false,
  postAuth: false,
  preAuthorization: false,
  postAuthorization: false,
  preLogout: false,
  postLogout: false,
  lastAuthorizationChangeTimestamp: 0,
  session: initialSessionState,
};

export const appLifecyclesReducer = createReducer(
  initialAppLifecyclesState,

  on(AppLifecyclesActions.initiateAuth, (state) => ({
    ...state,
    preAuth: true,
  })),
  on(AppLifecyclesActions.preAuthTasksCompleted, (state) => ({
    ...state,
    preAuth: false,
  })),
  on(AppLifecyclesActions.authCompleted, (state) => ({
    ...state,
    postAuth: true,
  })),
  on(AppLifecyclesActions.postAuthTasksCompleted, (state) => ({
    ...state,
    postAuth: false,
  })),

  on(AppLifecyclesActions.initiateAuthorization, (state) => ({
    ...state,
    preAuthorization: true,
  })),
  on(AppLifecyclesActions.preAuthorizationTasksCompleted, (state) => ({
    ...state,
    preAuthorization: false,
  })),
  on(AppLifecyclesActions.authorizationCompleted, (state) => ({
    ...state,
    postAuthorization: true,
  })),
  on(AppLifecyclesActions.postAuthorizationTasksCompleted, (state) => ({
    ...state,
    postAuthorization: false,
  })),

  on(AppLifecyclesActions.initiateLogout, (state) => ({
    ...state,
    preLogout: true,
  })),
  on(AppLifecyclesActions.preLogoutTasksCompleted, (state) => ({
    ...state,
    preLogout: false,
  })),
  on(AppLifecyclesActions.logoutCompleted, (state) => ({
    ...state,
    postLogout: true,
  })),
  on(AppLifecyclesActions.postLogoutTasksCompleted, (state) => ({
    ...state,
    postLogout: false,
  })),

  // Session inactivity (EDUS-2655)
  on(
    AppLifecyclesActions.sessionTrackingStarted,
    (state, { atMs, config }) => ({
      ...state,
      session: {
        lastActivityAt: atMs,
        nowMs: atMs,
        idleWarningAfterMs: config.idleWarningAfterMs,
        warningDurationMs: config.warningDurationMs,
      },
    })
  ),
  on(AppLifecyclesActions.sessionTrackingStopped, (state) => ({
    ...state,
    session: initialSessionState,
  })),
  on(AppLifecyclesActions.activityDetected, (state, { atMs }) => {
    // Nothing to extend before login — a stray DOM event on the welcome page
    // must not start a session clock.
    if (!isSessionTracking(state.session)) {
      return state;
    }

    if (isSessionWarning(state.session) || isSessionExpired(state.session)) {
      return state;
    }
    return {
      ...state,
      session: {
        ...state.session,
        // Math.max rather than assignment: peer-tab timestamps can arrive out
        // of order, and clocks can skew between tabs.
        lastActivityAt: Math.max(state.session.lastActivityAt, atMs),
      },
    };
  }),
  // A peer tab's extension carries the same weight as our own (AC 3.3).
  on(
    AppLifecyclesActions.sessionExtended,
    AppLifecyclesActions.peerSessionExtended,
    (state, { atMs }) =>
      isSessionTracking(state.session)
        ? {
            ...state,
            session: {
              ...state.session,
              lastActivityAt: Math.max(state.session.lastActivityAt, atMs),
              nowMs: Math.max(state.session.nowMs, atMs),
            },
          }
        : state
  ),
  on(AppLifecyclesActions.sessionTick, (state, { nowMs }) => ({
    ...state,
    session: {
      ...state.session,
      nowMs: Math.max(state.session.nowMs, nowMs),
    },
  }))
);
